
import { useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useOnboarding } from '@/hooks/use-onboarding';

export function AuthRedirect() {
  const { user, loading, initialized } = useAuth();
  const { hasCompletedOnboarding, loading: onboardingLoading } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Define public paths that don't require authentication
  const searchParams = new URLSearchParams(location.search);
  const hasClaimToken = searchParams.has('claim_token');
  
  const isPublicPath = 
    location.pathname === '/' ||
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/features' ||
    location.pathname === '/science' ||
    location.pathname === '/pricing' ||
    location.pathname === '/pricing-selection' ||
    location.pathname === '/contact' ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname === '/compare' ||
    location.pathname.startsWith('/compare/') ||
    location.pathname.startsWith('/book/') ||
    location.pathname === '/booking-confirmed' ||
    location.pathname.startsWith('/booking/') ||
    location.pathname.startsWith('/integrations/') ||
    // Allow onboarding access when there's a claim token (for prospect claiming)
    (location.pathname === '/onboarding' && hasClaimToken);

  useEffect(() => {
    // Don't attempt redirects until auth is initialized
    if (!initialized || loading) {
      return;
    }

    // If not authenticated and trying to access private page, redirect to login
    if (!user && !isPublicPath) {
      console.log("[AuthRedirect] Not authenticated, redirecting to login", {
        from: location.pathname,
        timestamp: new Date().toISOString()
      });
      navigate('/login', { 
        state: { from: location.pathname },
        replace: true
      });
      return;
    }
    
    // Only check onboarding status after auth is confirmed and onboarding data is loaded
    if (!onboardingLoading) {
      // Check if this is a wearable connection return
      const searchParams = new URLSearchParams(location.search);
      const isWearableConnectionReturn = searchParams.has('wearable_connected') || 
                                       searchParams.has('biostrap_connected') ||
                                       searchParams.has('calendar_connected');
      
      // Check if this is an OAuth error return (user is handling an error during onboarding)
      const isOAuthErrorReturn = searchParams.has('error') || 
                                searchParams.has('error_description') ||
                                searchParams.has('error_code');
      
      // If authenticated but hasn't completed onboarding, redirect to onboarding
      // UNLESS this is a wearable connection return or OAuth error return (let them continue their current progress)
      if (hasCompletedOnboarding === false && location.pathname !== '/onboarding' && !isWearableConnectionReturn && !isOAuthErrorReturn) {
        console.log("[AuthRedirect] Redirecting to onboarding", {
          from: location.pathname,
          hasCompletedOnboarding,
          currentPath: location.pathname,
          isWearableConnectionReturn,
          isOAuthErrorReturn,
          timestamp: new Date().toISOString()
        });
        
        // Add a small delay to prevent rapid redirects
        setTimeout(() => {
          navigate('/onboarding', { replace: true });
        }, 100);
        return;
      } else if (hasCompletedOnboarding === false && (isWearableConnectionReturn || isOAuthErrorReturn)) {
        console.log("[AuthRedirect] Preserving onboarding progress - wearable connection return or OAuth error", {
          isWearableConnectionReturn,
          isOAuthErrorReturn,
          timestamp: new Date().toISOString()
        });
        // Don't redirect - let them continue from their current onboarding step
      }
      
      // If user has completed onboarding and is on the onboarding page, redirect to app
      // BUT NOT if they're handling an OAuth error or wearable connection return
      if (hasCompletedOnboarding === true && location.pathname === '/onboarding' && !isOAuthErrorReturn && !isWearableConnectionReturn) {
        console.log("[AuthRedirect] Redirecting to app - user has completed onboarding", {
          isOAuthErrorReturn,
          isWearableConnectionReturn,
          timestamp: new Date().toISOString()
        });
        
        // Add a small delay to prevent rapid redirects
        setTimeout(() => {
          navigate('/meeting', { replace: true });
        }, 100);
        return;
      } else if (hasCompletedOnboarding === true && location.pathname === '/onboarding' && (isOAuthErrorReturn || isWearableConnectionReturn)) {
        console.log("[AuthRedirect] User completed onboarding but handling OAuth error or wearable connection - letting onboarding page handle it", {
          isOAuthErrorReturn,
          isWearableConnectionReturn,
          timestamp: new Date().toISOString()
        });
        // Don't redirect - let the onboarding page handle the OAuth error or wearable connection gracefully
      }
    } else {
      console.log("[AuthRedirect] Onboarding data still loading, skipping redirect checks");
    }

  }, [
    initialized,
    user, 
    loading, 
    onboardingLoading, 
    hasCompletedOnboarding, 
    navigate, 
    location.pathname, 
    isPublicPath
  ]);

  // Always render the outlet
  return <Outlet />;
}
